import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useCloseOpenShiftSuggestion } from "../../contexts/CloseOpenShiftSuggestionProvider";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useDayWeek } from "../../contexts/WeekView/useDayWeek";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { usePrepareNurseShift } from "../../hooks/usePrepareNurseShift";
import { useScheduleSaves } from "../../hooks/useScheduleSaves";
import { type Job, type Nurse, type NurseInShift, NurseInShiftCreationSource } from "../../utils/models";
import { findRedundantOpenShift } from "../../utils/openShiftMatching";
import type { ShiftWindow } from "../../utils/scheduleConflict";
import { QUICK_ADD_ANALYTICS_TIME_FORMAT, QUICK_ADD_SOURCE } from "./quickAddConsts";

type Params = {
	date: DateTime;
	showActual: boolean;
	nurse: Nurse | null;
	location: string | null;
	window: ShiftWindow;
	/** The job picked in the dialog; `undefined` leaves the one the shift builder stamps (the first). */
	job: Job | undefined;
	section: { name: string; locationKey: string };
	onClose: () => void;
};

type HospitalData = ReturnType<typeof useHospitalData>;
type Analytics = ReturnType<typeof useAnalytics>;
type RedundantOpenShift = ReturnType<typeof findRedundantOpenShift>;

type Deps = {
	t: TFunction;
	track: Analytics["track"];
	trackError: Analytics["trackError"];
	prepareNurseShift: ReturnType<typeof usePrepareNurseShift>;
	showErrorSnackbar: HospitalData["showErrorSnackbar"];
	addNurse: ReturnType<typeof useHandleNurseChange>["addNurse"];
	findRedundant: (nurse: Nurse, nurseToAdd: NurseInShift, location: string | null, window: ShiftWindow) => RedundantOpenShift;
	suggestCloseOpenShift: ReturnType<typeof useCloseOpenShiftSuggestion>;
	shiftTypeOfTimes: ReturnType<typeof useDayWeek>["shiftTypeOfTimes"];
	addNurseToShift: ReturnType<typeof useScheduleSaves>["addNurseToShift"];
	isDraftMode: boolean;
};

const buildNurseToAdd = (nurse: Nurse, shiftType: string, params: Params, deps: Deps): NurseInShift => {
	const { location, showActual, window, date, job } = params;
	const nurseToAdd = deps.prepareNurseShift({
		nurse,
		date,
		shiftType,
		location: location ?? undefined,
		addedFromActual: showActual,
		requestId: undefined,
		shiftTimes: { start: window.startTime, end: window.endTime, date },
		nurseInShiftId: undefined,
		shiftBonus: undefined,
		isDraftMode: false,
	});
	// The shift builder always stamps the nurse's first job; honour the picker instead so
	// the shift lands on the job whose shift lengths the time picker just offered.
	if (job) {
		nurseToAdd.jobId = job.id;
		nurseToAdd.jobName = job.displayName;
	}
	return nurseToAdd;
};

/** The add itself, closing only once it saved; a refusal is tracked, its translated reason shown, the form kept. */
const runQuickAdd = async (params: Params, deps: Deps): Promise<void> => {
	const { nurse, location, window, date, showActual, section, onClose } = params;
	if (!nurse) throw new Error("useQuickAddAdd: save reached with no person picked");
	const shiftType = deps.shiftTypeOfTimes(window);
	const nurseToAdd = buildNurseToAdd(nurse, shiftType, params, deps);
	const errorContext = { nurse_id: nurse.id, location, source: QUICK_ADD_SOURCE };
	// Resolved before the add, since adding refreshes the requests this reads from.
	const redundantOpenShift = deps.findRedundant(nurse, nurseToAdd, location, window);
	deps.track("quick_add_nurse_save_start", errorContext);
	try {
		await deps.addNurse({
			nurse: nurseToAdd,
			date,
			shiftType,
			location: location ?? undefined,
			isActual: showActual,
			source: NurseInShiftCreationSource.AddNurse,
		});
	} catch (error) {
		deps.trackError("quick_add_nurse_save_error", { ...errorContext, error_message: error instanceof Error ? error.message : String(error) });
		deps.showErrorSnackbar(deps.t("error.add.nurse"), errorContext, error);
		return;
	}
	// A draft saves nothing yet: the pending add on the day is what Publish sends.
	if (deps.isDraftMode) deps.addNurseToShift(nurseToAdd, date, shiftType, showActual, true);
	deps.track("quick_add_nurse_save", {
		nurse_id: nurse.id,
		job_id: nurseToAdd.jobId,
		location,
		section_name: section.name,
		section_location_key: section.locationKey,
		start_time: window.startTime.toFormat(QUICK_ADD_ANALYTICS_TIME_FORMAT),
		end_time: window.endTime.toFormat(QUICK_ADD_ANALYTICS_TIME_FORMAT),
	});
	// Before the prompt, which is hosted at the app root — otherwise this form stays painted under it.
	onClose();
	if (redundantOpenShift) await deps.suggestCloseOpenShift({ request: redundantOpenShift, source: QUICK_ADD_SOURCE });
};

/**
 * The "+" dialog's add: builds the new shift, publishes it, and closes only once it saved. A refusal (an overlap
 * the dialog could not see, say) shows the server's translated reason and keeps the form open with the user's
 * choices. Then offers to close an open shift the add made redundant.
 */
export const useQuickAddAdd = ({ date, showActual, nurse, location, window, job, section, onClose }: Params) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment, isHospital, showErrorSnackbar } = useHospitalData();
	const prepareNurseShift = usePrepareNurseShift();
	const { staffRequests } = useRequestsContext();
	const { isDraftMode } = useDraftMode();
	const suggestCloseOpenShift = useCloseOpenShiftSuggestion();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { addNurse, isLoading } = useHandleNurseChange();
	const { addNurseToShift } = useScheduleSaves();
	const { shiftTypeOfTimes } = useDayWeek(date);
	// Rebuilt only when a field changes, so the callbacks below are not invalidated on every render.
	const params = useMemo<Params>(
		() => ({ date, showActual, nurse, location, window, job, section, onClose }),
		[date, showActual, nurse, location, window, job, section, onClose],
	);

	const findRedundant = useCallback(
		(nurse: Nurse, nurseToAdd: NurseInShift, location: string | null, window: ShiftWindow): RedundantOpenShift =>
			params.showActual || isDraftMode || isHospital
				? undefined
				: findRedundantOpenShift(staffRequests, {
						departmentId: chosenDepartment.department_id,
						shiftStart: window.startTime,
						shiftEnd: window.endTime,
						location: location ?? "",
						jobId: nurseToAdd.jobId,
						role: nurse.role,
						jobManagementEnabled,
						isCharge: nurseToAdd.charge,
					}),
		[params.showActual, isDraftMode, isHospital, staffRequests, chosenDepartment.department_id, jobManagementEnabled],
	);

	const performAdd = useCallback(
		() =>
			runQuickAdd(params, {
				t,
				track,
				trackError,
				prepareNurseShift,
				showErrorSnackbar,
				addNurse,
				findRedundant,
				suggestCloseOpenShift,
				shiftTypeOfTimes,
				addNurseToShift,
				isDraftMode,
			}),
		[
			params,
			t,
			track,
			trackError,
			prepareNurseShift,
			showErrorSnackbar,
			addNurse,
			findRedundant,
			suggestCloseOpenShift,
			shiftTypeOfTimes,
			addNurseToShift,
			isDraftMode,
		],
	);

	return { performAdd, isAdding: isLoading.addNurse };
};
